/**
 * Client Context
 * Manages current client state for productized service model.
 * Allows switching between clients and filtering data by client.
 */

import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from './supabase';
import { useAuth } from './auth-context';

export interface Client {
  id: string;
  user_id: string;
  name: string;
  company_name: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  notes: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Computed counts (from API)
  inbox_connection_count?: number;
  prospect_count?: number;
}

interface ClientContextType {
  /** Currently selected client, or null if no clients exist */
  currentClient: Client | null;
  /** All clients owned by the user */
  clients: Client[];
  /** Whether clients are being loaded */
  loading: boolean;
  /** Switch to a specific client */
  switchClient: (clientId: string | null) => void;
  /** Refresh the client list from the server */
  refreshClients: () => Promise<void>;
  /** Get the current client ID for filtering queries */
  currentClientId: string | null;
  /** Whether the client management feature is enabled (can be gated by plan) */
  clientManagementEnabled: boolean;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

const STORAGE_KEY = 'prozpector_current_client_id';

export function ClientProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [currentClient, setCurrentClient] = useState<Client | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);

  // TODO: Gate by subscription plan in the future
  // const clientManagementEnabled = user?.subscription?.plan === 'agency';
  const clientManagementEnabled = true;

  const fetchClients = useCallback(async () => {
    if (!user) {
      setClients([]);
      setCurrentClient(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('user_id', user.id)
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;

      setClients(data || []);

      // Restore selected client from localStorage
      const savedClientId = localStorage.getItem(STORAGE_KEY);
      if (savedClientId === 'all') {
        // "All Clients" mode - show all data regardless of client_id
        setCurrentClient(null);
      } else if (savedClientId) {
        const savedClient = data?.find(c => c.id === savedClientId);
        if (savedClient) {
          setCurrentClient(savedClient);
        } else {
          // Saved client no longer exists, default to "All Clients"
          localStorage.setItem(STORAGE_KEY, 'all');
          setCurrentClient(null);
        }
      } else {
        // No saved preference, default to "All Clients" to show everything
        localStorage.setItem(STORAGE_KEY, 'all');
        setCurrentClient(null);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const switchClient = useCallback((clientId: string | null) => {
    if (clientId === null || clientId === 'all') {
      // "All Clients" mode - show all data
      setCurrentClient(null);
      localStorage.setItem(STORAGE_KEY, 'all');
    } else {
      const client = clients.find(c => c.id === clientId);
      if (client) {
        setCurrentClient(client);
        localStorage.setItem(STORAGE_KEY, clientId);
      }
    }
  }, [clients]);

  const refreshClients = useCallback(async () => {
    setLoading(true);
    await fetchClients();
  }, [fetchClients]);

  // Fetch clients when user changes
  useEffect(() => {
    fetchClients();
  }, [fetchClients]);

  // Update currentClient when clients list changes
  useEffect(() => {
    if (currentClient) {
      const updated = clients.find(c => c.id === currentClient.id);
      if (updated) {
        setCurrentClient(updated);
      } else {
        // Current client was deleted, switch to "All Clients"
        setCurrentClient(null);
        localStorage.setItem(STORAGE_KEY, 'all');
      }
    }
    // If currentClient is null, that's "All Clients" mode - no action needed
  }, [clients, currentClient?.id]);

  return (
    <ClientContext.Provider
      value={{
        currentClient,
        clients,
        loading,
        switchClient,
        refreshClients,
        currentClientId: currentClient?.id || null,
        clientManagementEnabled,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
}

export function useClient() {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
}

/**
 * Hook to get the current client ID for use in queries.
 * Returns null only when no clients exist.
 */
export function useClientId(): string | null {
  const { currentClientId } = useClient();
  return currentClientId;
}

/**
 * Hook to add client_id filter to Supabase queries.
 * Returns a function that adds the filter if a client is selected.
 *
 * Usage:
 * ```
 * const addClientFilter = useClientFilter();
 * let query = supabase.from('prospects').select('*');
 * query = addClientFilter(query);
 * ```
 */
export function useClientFilter() {
  const { currentClientId } = useClient();

  return useCallback(<T extends { eq: (column: string, value: string) => T }>(
    query: T
  ): T => {
    if (currentClientId) {
      return query.eq('client_id', currentClientId);
    }
    return query;
  }, [currentClientId]);
}
